import { useState, useEffect, useRef } from "react";
import PortalConfetti from "./PortalConfetti";
import { doc, setDoc, getDoc, increment } from "firebase/firestore";
import { db } from "../firebase";
import { QUOTES_MAP } from "../maps";
import { CHARACTERS, QUOTES } from "../data";
import { getDailyItem, updateGameStats } from "../utils/gameLogic";
import { usePersistentStorage, useKeyboardNavigation } from "../hooks/useGameHooks";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Quote guessing mode — 6 guesses to identify the speaker of a daily quote.
 * Progressive hints reveal the game (after 3 wrong guesses) and recipient (after 4).
 *
 * @param {{ version: string, targetDateObj: Date, targetDateStr: string, isArchive: boolean, setIsModalOpen: (v: boolean) => void, guessedCharacters: Array<Object>, setGuessedCharacters: (g: Array<Object>) => void }} props
 * @param {string} props.version - "old" or "new".
 * @param {Date} props.targetDateObj - Target date object.
 * @param {string} props.targetDateStr - Date string for localStorage keys.
 * @param {boolean} props.isArchive - Whether viewing an archived date.
 * @param {(v: boolean) => void} props.setIsModalOpen - Opens global stats modal.
 * @param {Array<Object>} props.guessedCharacters - Previously submitted character guesses.
 * @param {(g: Array<Object>) => void} props.setGuessedCharacters - Guess state setter.
 * @returns {JSX.Element} The quote game UI.
 */
export default function QuoteMode({
  version,
  targetDateObj,
  targetDateStr,
  isArchive,
  setIsModalOpen,
  guessedCharacters,
  setGuessedCharacters,
}) {
  const [currentGuess, setCurrentGuess] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [collection, setCollection] = usePersistentStorage(
    "kisekidle-collection-quotes",
    []
  );
  const [isCopied, setIsCopied] = useState(false);

  const [targetQuote] = useState(
    getDailyItem(QUOTES, targetDateObj, QUOTES_MAP)
  );

  const MAX_GUESSES = 6;
  const wrongGuessesCount = guessedCharacters.filter(
    (char) => char.name !== targetQuote.character
  ).length;
  const hasWon = guessedCharacters.some(
    (char) => char.name === targetQuote.character
  );
  const guessesLeft = MAX_GUESSES - guessedCharacters.length;
  const isGameOver = guessesLeft <= 0 && !hasWon;

  // --- ANTI-CHEAT WIN FLAG ---
  useEffect(() => {
    if (hasWon) {
      const storageKey = `kisekidle-quotes-${targetDateStr}`;
      const storedData = JSON.parse(localStorage.getItem(storageKey) || "{}");
      storedData.hasWon = true;
      localStorage.setItem(storageKey, JSON.stringify(storedData));

      // Sync to collection if missing
      const selectedId = String(targetQuote.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection((prev) => [...prev, selectedId]);
      }
    }
  }, [hasWon, targetDateStr, targetQuote.character]);

  const [isGameRevealed, setIsGameRevealed] = useState(false);
  const [isRecipientRevealed, setIsRecipientRevealed] = useState(false);

  const guessesNeededForGame = Math.max(0, 3 - wrongGuessesCount);
  const guessesNeededForRecipient = Math.max(0, 4 - wrongGuessesCount);

  const [globalStats, setGlobalStats] = useState(null);

  /** Filters characters by search query, excluding already-guessed ones, and sorts results. */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setCurrentGuess(value);
    setFocusedIndex(-1);
    if (value.length > 0) {
      const query = value.toLowerCase().trim();
      const filteredChars = CHARACTERS.filter((char) =>
        char.name.toLowerCase().includes(query) &&
        !guessedCharacters.some((g) => g.name === char.name)
      ).sort((a, b) => {
        const nameA = a.name.toLowerCase();
        const nameB = b.name.toLowerCase();

        const aStartsWith = nameA.startsWith(query);
        const bStartsWith = nameB.startsWith(query);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return nameA.localeCompare(nameB);
      });
      setSuggestions(filteredChars.slice(0, 15));
    } else {
      setSuggestions([]);
    }
  };

  /** Processes a character guess — checks win, updates stats, adds to collection. */
  const handleGuess = (selectedChar) => {
    if (hasWon || isGameOver) return;
    if (guessedCharacters.find((c) => c.name === selectedChar.name)) return;

    const newGuesses = [...guessedCharacters, selectedChar];
    const isWin = selectedChar.name === targetQuote.character;
    const isLoss = newGuesses.length >= MAX_GUESSES && !isWin;

    if (!isArchive && (isWin || isLoss)) {
      updateGameStats("quote", isWin, newGuesses.length, MAX_GUESSES);
    }

    if (isWin) {
      const selectedId = String(targetQuote.id);
      if (!collection.some((id) => String(id) === selectedId)) {
        setCollection([...collection, selectedId]);
      }
    }
    setGuessedCharacters(newGuesses);
    setCurrentGuess("");
    setSuggestions([]);
  };

  const { focusedIndex, setFocusedIndex, handleKeyDown, listRef } =
    useKeyboardNavigation(suggestions, handleGuess);

  const syncInProgress = useRef(false);

  // --- QUOTE MODE TRACKING ---
  useEffect(() => {
    const syncReceipt = `synced-quote-${targetDateStr}`;
    const hasAlreadySynced = localStorage.getItem(syncReceipt);

    if ((hasWon || isGameOver) && !hasAlreadySynced && !syncInProgress.current) {
      const updateAndFetchStats = async () => {
        syncInProgress.current = true;
        try {
          const statsRef = doc(db, "quote_stats", targetDateStr);
          const updateData = {
            totalPlays: increment(1),
            totalWins: hasWon ? increment(1) : increment(0),
            totalWinningAttempts: hasWon
              ? increment(guessedCharacters.length)
              : increment(0),
            guessTally: {},
          };
          guessedCharacters.forEach((char) => {
            if (char && char.name)
              updateData.guessTally[char.name] = increment(1);
          });
          await setDoc(statsRef, updateData, { merge: true });
          localStorage.setItem(syncReceipt, "true");
          const updatedDoc = await getDoc(statsRef);
          if (updatedDoc.exists()) setGlobalStats(updatedDoc.data());
        } catch (error) {
          console.error("Firebase Error:", error);
        } finally {
          syncInProgress.current = false;
        }
      };
      updateAndFetchStats();
    }
  }, [hasWon, isGameOver, guessedCharacters.length, targetDateStr]);

  /** Copies quote-mode results (emoji or text format) to clipboard. */
  const handleShare = (format) => {
    const score = hasWon ? guessedCharacters.length : "X";
    let shareText = `Kisekidle - Quote (${targetDateStr})\nGuesses: ${score}/${MAX_GUESSES}\n\n`;
    
    if (format === "emoji") {
      const emojis = guessedCharacters
        .map((char) => (char.name === targetQuote.character ? "🟩" : "🟥"))
        .join("");
      shareText += emojis;
    } else {
      guessedCharacters.forEach((char, i) => {
        shareText += `${i + 1}. ${char.name} ${char.name === targetQuote.character ? "✅" : "❌"}\n`;
      });
    }

    shareText += `\n\nhttps://kisekidle.app/`;

    navigator.clipboard.writeText(shareText).then(() => {
      setIsCopied(format);
      setTimeout(() => setIsCopied(null), 2000);
    });
  };

  return (
    <div className="game-terminal quote-terminal">
      <div className="header-container">
        <h2 className="mode-title">Who said it?</h2>
        <div className="guesses-remaining">
          Guesses Remaining: {guessesLeft}
        </div>
      </div>

      <motion.div 
        className="quote-box"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 20 }}
      >
        "{targetQuote.quote}"
      </motion.div>

      {hasWon && (
        <div className="victory-box">
          <PortalConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={400}
            gravity={0.15}
          />
          <h3>🎉 You got it! 🎉</h3>
          <p>
            The character was <strong>{targetQuote.character}</strong>.
          </p>
          {targetQuote.trailsDbUrl && (
            <div style={{ margin: "10px 0 15px 0" }}>
              <a
                href={targetQuote.trailsDbUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#4a90e2",
                  textDecoration: "underline",
                  fontSize: "0.9rem",
                }}
              >
                📖 View context in Trails in the Database
              </a>
            </div>
          )}
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      {isGameOver && (
        <div className="game-over-box">
          <h3>❌ Out of guesses! ❌</h3>
          <p>
            The correct character was <strong>{targetQuote.character}</strong>.
          </p>
          {targetQuote.trailsDbUrl && (
            <div style={{ margin: "10px 0 15px 0" }}>
              <a
                href={targetQuote.trailsDbUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#4a90e2",
                  textDecoration: "underline",
                  fontSize: "0.9rem",
                }}
              >
                📖 View context in Trails in the Database
              </a>
            </div>
          )}
          <div className="share-buttons-container">
            <button
              onClick={() => setIsModalOpen(true)}
              className="share-all-btn global-stats-trigger"
            >
              📊 View Global Stats
            </button>

            <button className="share-all-btn" onClick={() => handleShare("emoji")}>
              {isCopied === "emoji" ? "📋 Copied!" : "📤 Share Emojis"}
            </button>

            <button className="share-all-btn" onClick={() => handleShare("text")}>
              {isCopied === "text" ? "📋 Copied!" : "📤 Share Text"}
            </button>
          </div>
        </div>
      )}

      <div className="hints-container">
        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isGameRevealed || hasWon || isGameOver
                ? "revealed"
                : guessesNeededForGame <= 0
                  ? "unlocked"
                  : "locked"
              }`}
            onClick={() => guessesNeededForGame <= 0 && setIsGameRevealed(true)}
            disabled={
              guessesNeededForGame > 0 || isGameRevealed || hasWon || isGameOver
            }
          >
            {isGameRevealed || hasWon || isGameOver
              ? `Game: ${targetQuote.game}`
              : "Reveal Game"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForGame > 0
                ? `Available after ${guessesNeededForGame} more guess${guessesNeededForGame > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>

        <div className="hint-wrapper">
          <button
            className={`hint-btn ${isRecipientRevealed || hasWon || isGameOver
                ? "revealed"
                : guessesNeededForRecipient <= 0
                  ? "unlocked"
                  : "locked"
              }`}
            onClick={() =>
              guessesNeededForRecipient <= 0 && setIsRecipientRevealed(true)
            }
            disabled={
              guessesNeededForRecipient > 0 ||
              isRecipientRevealed ||
              hasWon ||
              isGameOver
            }
          >
            {isRecipientRevealed || hasWon || isGameOver
              ? `To: ${targetQuote.recipient}`
              : "Reveal Recipient"}
          </button>

          {!(hasWon || isGameOver) && (
            <div className="hint-condition">
              {guessesNeededForRecipient > 0
                ? `Available after ${guessesNeededForRecipient} more guess${guessesNeededForRecipient > 1 ? "es" : ""
                }`
                : "Unlocked!"}
            </div>
          )}
        </div>
      </div>

      {!hasWon && !isGameOver && (
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            value={currentGuess}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a character name..."
            autoFocus
          />
          <button
            className="submit-button"
            onClick={() =>
              suggestions.length > 0 && handleGuess(suggestions[0])
            }
          >
            Submit Guess
          </button>
          {suggestions.length > 0 && (
            <ul className="suggestions-list" ref={listRef}>
              {suggestions.map((item, index) => (
                <li
                  key={item.id}
                  className={`suggestion-item ${index === focusedIndex ? "focused" : ""}`}
                  onClick={() => handleGuess(item)}
                  onMouseEnter={() => setFocusedIndex(index)}
                >
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name || item.displayTitle || "Character"}
                      loading="lazy"
                      className="suggestion-image"
                    />
                  )}
                  <span>{item.name || item.displayTitle || item.locationName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <ul className="guess-list">
        <AnimatePresence initial={false}>
          {guessedCharacters
            .slice()
            .reverse()
            .map((char, index) => {
              const isCorrect = char.name === targetQuote.character;
              return (
                <motion.li
                  key={char.id || index}
                  className={`guess-item ${isCorrect ? "correct" : "incorrect"}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 25 }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {char.imageUrl && (
                      <img
                        src={char.imageUrl}
                        alt={char.name}
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "8px",
                          objectFit: "cover",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <strong>{char.name}</strong>
                  </div>
                  <span>{isCorrect ? "🎉 Correct!" : "❌ Incorrect"}</span>
                </motion.li>
              );
            })}
        </AnimatePresence>
      </ul>
    </div>
  );
}
